import { useSelector, useDispatch } from 'react-redux';
import { ShieldAlert, LogOut } from 'lucide-react';
import { logout } from '../../store/slices/authSlice';

// MER-02: a suspended merchant can still sign in, but the dashboard is locked
// behind this blocking popup until support reactivates the account.
const SuspendedModal = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  if (!isAuthenticated || user?.status !== 'suspended') return null;

  const reason = user?.suspensionReason;

  const handleLogout = () => {
    dispatch(logout());
    window.location.href = '/login';
  };

  return (
    <div className="suspended-overlay" role="dialog" aria-modal="true" aria-labelledby="suspended-title">
      <div className="suspended-card">
        <div className="suspended-icon"><ShieldAlert size={28} /></div>
        <h2 id="suspended-title">Your account is blocked</h2>
        <p className="suspended-lead">
          Your MaxelPay account has been suspended, so your dashboard and payments are paused.
        </p>
        {reason && (
          <div className="suspended-reason">
            <span className="suspended-reason-label">Reason</span>
            <span className="suspended-reason-text">{reason}</span>
          </div>
        )}
        <p className="suspended-support">
          To restore access, contact our support team at{' '}
          <a href="mailto:support@maxelpay.com">support@maxelpay.com</a>.
        </p>
        <button className="suspended-logout" onClick={handleLogout}>
          <LogOut size={16} /> Log out
        </button>
      </div>

      <style>{`
        .suspended-overlay {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          background: rgba(15, 18, 34, 0.72);
          backdrop-filter: blur(4px);
        }
        .suspended-card {
          width: 100%; max-width: 440px;
          background: var(--surface, #fff); border-radius: 16px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.35);
          padding: 32px 28px; text-align: center;
        }
        .suspended-icon {
          width: 56px; height: 56px; margin: 0 auto 16px;
          display: grid; place-items: center; border-radius: 14px;
          background: #fee2e2; color: #dc2626;
        }
        .suspended-card h2 {
          margin: 0 0 8px; font-size: 20px; font-weight: 700;
          color: var(--ink, #16182e);
        }
        .suspended-lead { margin: 0 0 16px; font-size: 14px; color: var(--t-500, #475569); line-height: 1.5; }
        .suspended-reason {
          display: flex; flex-direction: column; gap: 4px; text-align: left;
          background: var(--bg-inset, #f1f5f9); border: 1px solid var(--line, #e2e8f0);
          border-radius: 10px; padding: 12px 14px; margin-bottom: 16px;
        }
        .suspended-reason-label {
          font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
          text-transform: uppercase; color: var(--t-400, #94a3b8);
        }
        .suspended-reason-text { font-size: 14px; color: var(--ink, #16182e); }
        .suspended-support { margin: 0 0 22px; font-size: 13px; color: var(--t-500, #475569); }
        .suspended-support a { color: var(--brand-600, #6366f1); font-weight: 600; }
        .suspended-logout {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; min-height: 44px; padding: 0 20px;
          border: 1px solid var(--line, #e2e8f0); border-radius: 10px;
          background: var(--surface, #fff); color: var(--ink, #16182e);
          font-size: 14px; font-weight: 600; cursor: pointer;
        }
        .suspended-logout:hover { background: var(--surface-2, #f8fafc); }
      `}</style>
    </div>
  );
};

export default SuspendedModal;
